import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ExternalLink, Info, Video } from 'lucide-react';
import '../components/aiinterview/aiinterview.css';
import '../components/videoroom/videoroom.css';
import { useAuth } from '../context/AuthContext';
import { errMsg } from '../lib/aiInterviewApi';
import { candidateRoomAPI } from '../lib/roomApi';
import { browserTz } from '../lib/schedulingApi';
import { useMediaStream } from '../components/aiinterview/media';
import { useDailyCall } from '../components/videoroom/useDailyCall';
import CallStage from '../components/videoroom/CallStage';
import PreJoin from '../components/videoroom/PreJoin';
import { fmtClockTime, fmtCountdown, useNow } from '../components/videoroom/time';

const STAFF = ['recruiter', 'admin', 'super_admin'];

const Shell = ({ ctx, children, solo = true }) => (
  <div className="iv vr-room" data-testid="candidate-join-page">
    <header className="vr-top">
      <div className="iv-row" style={{ gap: '0.6rem' }}>
        <img src="/chire-icon.png" alt="CHIRE" width={28} height={28} style={{ borderRadius: 6 }} />
        <div><h1>{ctx?.title || 'Interview'}{ctx?.job_title ? `: ${ctx.job_title}` : ''}</h1>{ctx?.company_name && <small>{ctx.company_name}</small>}</div>
      </div>
    </header>
    <div className={`vr-body ${solo ? 'is-solo' : ''}`}><main className="vr-main">{children}</main></div>
  </div>
);

const Consent = ({ value, onChange }) => (
  <div className="vr-consent" data-testid="join-consent">
    <b style={{ fontSize: '0.9rem' }}>Live transcription</b>
    <span className="vr-muted">CHIRE can transcribe what you say during the call so the interviewers can take accurate notes. It is only seen by the hiring team. The call itself is not recorded. Your choice doesn't affect how you are assessed.</span>
    <div className="iv-options">
      <button type="button" className={`iv-option ${value === true ? 'is-on' : ''}`} onClick={() => onChange(true)} data-testid="join-consent-yes"><b>Yes, transcribe</b><span>Helps the interviewers capture your answers</span></button>
      <button type="button" className={`iv-option ${value === false ? 'is-on' : ''}`} onClick={() => onChange(false)} data-testid="join-consent-no"><b>No, thanks</b><span>Join without transcription</span></button>
    </div>
  </div>
);

function Lobby({ ctx, token, media, dc, onJoined }) {
  const now = useNow();
  const tz = ctx.candidate_timezone || browserTz();
  const [agree, setAgree] = useState(ctx.consent ? ctx.consent.transcription : null);
  const [busy, setBusy] = useState(false);
  const join = async () => {
    setBusy(true);
    try {
      const { data } = await candidateRoomAPI.join(token, agree);
      media.stop();
      await dc.join({ url: data.room_url, token: data.token });
      onJoined();
    } catch (e) {
      toast.error(errMsg(e, 'Could not join the interview'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="vr-card vr-prejoin" data-testid="join-lobby">
      <h2>{dc.state === 'left' ? 'You left the interview' : `Hi ${ctx.candidate_first_name || 'there'}, let's check your camera and mic`}</h2>
      <p className="vr-muted">{fmtClockTime(ctx.start, tz)} ({tz}) · {ctx.duration_min} minutes{ctx.interviewers.length ? ` · with ${ctx.interviewers.join(', ')}` : ''}</p>
      {ctx.state === 'early' && <p className="vr-muted" data-testid="join-early">The room opens 15 minutes before the start. <span className="vr-count" data-testid="join-countdown">{fmtCountdown(ctx.opens_at, now)}</span></p>}
      {dc.error && <div className="iv-note is-bad" style={{ margin: '0.6rem 0' }} data-testid="join-call-error">{dc.error}</div>}
      <div className="vr-lobby">
        <PreJoin media={media} />
        <div>
          <Consent value={agree} onChange={setAgree} />
          <div className="iv-row-end">
            <button type="button" className="iv-btn iv-btn-cta" disabled={ctx.state !== 'open' || agree === null || busy} onClick={join} data-testid="join-btn">
              <Video size={16} /> {busy ? 'Joining…' : dc.state === 'left' ? 'Rejoin' : 'Join interview'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Ended = ({ title, body, testId, link }) => (
  <div className="vr-card" data-testid={testId}>
    <h2>{title}</h2><p className="vr-muted">{body}</p>
    {link && <a className="iv-btn iv-btn-cta" style={{ marginTop: '0.8rem' }} href={link} target="_blank" rel="noreferrer" data-testid="join-external-link"><ExternalLink size={16} /> Open meeting link</a>}
  </div>
);

export default function CandidateJoinPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const [ctx, setCtx] = useState(null);
  const [missing, setMissing] = useState(false);
  const media = useMediaStream();
  const dc = useDailyCall();
  const load = useCallback(() => candidateRoomAPI.context(token).then(({ data }) => setCtx(data))
    .catch((e) => (e.response?.status === 404 ? setMissing(true) : null)), [token]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    if (user && STAFF.includes(user.role)) candidateRoomAPI.staffPath(token).then(({ data }) => nav(data.path, { replace: true })).catch(() => {});
  }, [user, token, nav]);

  if (missing) return <Shell><Ended title="This link isn't valid" body="Please check the link in your email, or contact the recruiter." testId="join-invalid" /></Shell>;
  if (!ctx) return <Shell><p className="vr-muted">Loading…</p></Shell>;
  if (dc.state === 'joined') {
    return (
      <Shell ctx={ctx}>
        {ctx.consent?.transcription && <div className="vr-banner" data-testid="join-transcribing-banner"><Info size={14} /> Live transcription is on to help the interviewers take notes.</div>}
        <CallStage dc={dc} onLeave={dc.leave} emptyText="You're in. The interviewer will join shortly." />
      </Shell>
    );
  }
  let body;
  if (ctx.venue !== 'chire') body = <Ended title="Your interview uses a meeting link" body="Use the link from the hiring team to join." link={ctx.meeting_link} testId="join-external" />;
  else if (ctx.status === 'cancelled' || ctx.state === 'cancelled') body = <Ended title="This interview was cancelled" body="The hiring team will be in touch about next steps." testId="join-cancelled" />;
  else if (ctx.state === 'closed' || (['completed', 'no_show'].includes(ctx.status) && ctx.state !== 'open')) body = <Ended title="This interview has ended" body="Thanks for your time. The hiring team will be in touch." testId="join-closed" />;
  else if (ctx.state === 'not_booked') body = <Ended title="No time is booked yet" body="Use the scheduling link in your email to pick a time." testId="join-unbooked" />;
  else body = <Lobby ctx={ctx} token={token} media={media} dc={dc} onJoined={load} />;
  return <Shell ctx={ctx}>{body}</Shell>;
}
